import { useQueryClient, QueryClient } from "react-query"
import { RECOMMENDATIONS_QUERY_KEY } from "./useCarrotPlanRecommendationsQuery"
import { CarrotPlanMediaItem, CarrotPlanOptions, CarrotPlanRecommendation } from "types/CarrotPlanOptions"
import { JourneyData, Step } from "./useCreateCarrotPlan"
import { resolveMenuItemDisplayLabel } from "../MediaTypes"
import { CARROT_PLAN_OPTIONS_QUERY_KEY } from "./useCarrotPlanOptions"
import { LabeledItem, RecommendationsDiff, SlotChange } from "types/RecommendationsDiff"

type LabeledItems = { recs: LabeledItem[]; steps: LabeledItem[] }

type MenuItemLookup = { entityId: string; displayText: string }

type MenuItemMap = Map<string, MenuItemLookup>

type MediaItemMap = Map<string, CarrotPlanMediaItem>

type LookupMaps = { menuItemsById: MenuItemMap; mediaItemsById: MediaItemMap }

type UseRecommendationsDiff = {
  getRecommendationsDiff: (planSteps: Step[], journeyData: JourneyData) => RecommendationsDiff
}

const toKey = (r: LabeledItem) => `${r.menuItemId}|${r.mediaItemId ?? ""}`
const itemsAreSame = (a: LabeledItem, b: LabeledItem) => toKey(a) === toKey(b)

function buildLookupMaps(options: CarrotPlanOptions | undefined): LookupMaps {
  const menuItemsById: MenuItemMap = new Map()
  options?.menuOptions?.forEach((category) =>
    category.menuItems.forEach((item) =>
      menuItemsById.set(item.entityId, { entityId: item.entityId, displayText: item.displayText })
    )
  )

  const mediaItemsById: MediaItemMap = new Map()
  options?.mediaOptions?.forEach((category) =>
    category.subCategories.forEach((sub) => sub.mediaItems.forEach((media) => mediaItemsById.set(media.entryId, media)))
  )

  return { menuItemsById, mediaItemsById }
}

function getLabeledItems(
  queryClient: QueryClient,
  employeeId: number,
  planSteps: Step[],
  journeyData: JourneyData
): LabeledItems | null {
  const { primaryJourneyId, primaryJourneyStageId } = journeyData

  const recsCache = queryClient.getQueryData<CarrotPlanRecommendation[]>([
    RECOMMENDATIONS_QUERY_KEY,
    employeeId,
    primaryJourneyId,
    primaryJourneyStageId
  ])

  if (!recsCache?.length) return null

  const optionsCache = queryClient.getQueryData<CarrotPlanOptions>([
    CARROT_PLAN_OPTIONS_QUERY_KEY,
    employeeId,
    primaryJourneyId,
    primaryJourneyStageId
  ])

  const { menuItemsById, mediaItemsById } = buildLookupMaps(optionsCache)

  const toLabeledItem = (menuItemId: string, mediaItemId: string | null): LabeledItem => {
    const menuItem = menuItemsById.get(menuItemId)
    const mediaItem = mediaItemId ? mediaItemsById.get(mediaItemId) ?? null : null
    const label = menuItem
      ? resolveMenuItemDisplayLabel(menuItemId, menuItem.displayText, mediaItem?.title)
      : menuItemId
    return { menuItemId, mediaItemId, label }
  }

  return {
    recs: recsCache.map((r) => toLabeledItem(r.menuItemId, r.mediaItemId ?? null)),
    steps: planSteps.map((s) => toLabeledItem(s.menuItemId, s.entryId ?? null))
  }
}

function arraysContainSameItems(arrA: LabeledItem[], arrB: LabeledItem[]): boolean {
  if (arrA.length !== arrB.length) return false
  const keysA = arrA.map(toKey).sort()
  const keysB = arrB.map(toKey).sort()
  return keysA.every((key, i) => key === keysB[i])
}

function arraysAreInSameOrder(arrA: LabeledItem[], arrB: LabeledItem[]): boolean {
  if (arrA.length !== arrB.length) return false
  return arrA.every((item, i) => itemsAreSame(item, arrB[i]))
}

function compareItemArrays({ recs, steps }: LabeledItems): "unchanged" | "reordered" | null {
  if (!arraysContainSameItems(recs, steps)) return null
  return arraysAreInSameOrder(recs, steps) ? "unchanged" : "reordered"
}

function didOtherItemsReorder(recs: LabeledItem[], steps: LabeledItem[]): boolean {
  const stepKeys = new Set(steps.map(toKey))
  const recKeys = new Set(recs.map(toKey))
  const retainedRecs = recs.filter((item) => stepKeys.has(toKey(item)))
  const retainedSteps = steps.filter((item) => recKeys.has(toKey(item)))
  return !arraysAreInSameOrder(retainedRecs, retainedSteps)
}

// Items kept from the original recommendations, in final (submitted) order.
function getRetainedItems({ recs, steps }: LabeledItems): LabeledItem[] {
  const recKeySet = new Set(recs.map(toKey))
  return steps.filter((item) => recKeySet.has(toKey(item)))
}

function evaluateSlotChanges({ recs, steps }: LabeledItems): SlotChange[] {
  const recKeySet = new Set(recs.map(toKey))
  const stepKeySet = new Set(steps.map(toKey))
  const slots = new Map<number, { original?: LabeledItem; replacement?: LabeledItem }>()

  recs.forEach((item, position) => {
    if (!stepKeySet.has(toKey(item))) {
      slots.set(position, { ...slots.get(position), original: item })
    }
  })

  steps.forEach((item, position) => {
    if (!recKeySet.has(toKey(item))) {
      slots.set(position, { ...slots.get(position), replacement: item })
    }
  })

  const changes: SlotChange[] = []
  slots.forEach(({ original, replacement }, position) => {
    if (original && replacement) changes.push({ changeType: "replaced", position, original, replacement })
    else if (original) changes.push({ changeType: "removed", position, original })
    else if (replacement) changes.push({ changeType: "added", position, replacement })
  })

  return changes
}

export function useRecommendationsDiff(employeeId: number): UseRecommendationsDiff {
  const queryClient = useQueryClient()

  function getRecommendationsDiff(planSteps: Step[], journeyData: JourneyData): RecommendationsDiff {
    const labeledItems = getLabeledItems(queryClient, employeeId, planSteps, journeyData)
    if (!labeledItems) return { status: "no-recommendations" }
    const { recs, steps } = labeledItems
    const retainedItems = getRetainedItems({ recs, steps })

    const matchStatus = compareItemArrays({ recs, steps })
    if (matchStatus) return { status: matchStatus, retainedItems }

    const changes = evaluateSlotChanges({ recs, steps })

    return {
      status: "modified",
      changes,
      hasReorderedRecommendations: didOtherItemsReorder(recs, steps),
      retainedItems
    }
  }

  return { getRecommendationsDiff }
}
